import { FeatureCollection } from "geojson";
import type { Job } from "../../../../../../api/types";
import { GeoJSONOptionsKey, GeoJSONFileKey, GroupedGeoJSONOption } from "../../../ImportTable.types";

/** Supported download formats for GeoJSON files */
export type GeoJSONDownloadFormat = "shapefile" | "kml" | "kmz" | "geojson";

/** Format option for download menu */
export interface DownloadFormatOption {
	label: string;
	value: GeoJSONDownloadFormat;
	extension: string;
	successMessage: string;
}

/** Constant array of format options for the download menu */
export const DOWNLOAD_FORMAT_OPTIONS: DownloadFormatOption[] = [
	{ label: "Download as Shapefile", value: "shapefile", extension: "zip", successMessage: "Shapefile downloaded" },
	{ label: "Download as KML", value: "kml", extension: "kml", successMessage: "KML file downloaded" },
	{ label: "Download as KMZ", value: "kmz", extension: "kmz", successMessage: "KMZ file downloaded" },
	{ label: "Download as GeoJSON", value: "geojson", extension: "json", successMessage: "GeoJSON file downloaded" },
];

// Map geoJSONKey to valid FileOptionType for uploaded files
export type GeoJSONOptionType = "Boundary" | "Centroids" | "Zones";

export const geoJSONKeyToOptionType: Record<GeoJSONFileKey, GeoJSONOptionType> = {
	boundaryGeoJSON: "Boundary",
	pointsGeoJSON: "Centroids",
	zonesGeoJSON: "Zones",
};

/**
 * ViewModel interface for GeoJSON file column
 * Provides all business logic functions needed by the view layer
 */
export interface GeoJSONFileColumnViewModel {
	/** Flatten GeoJSONOptionsGroup[] into GroupedGeoJSONOption[] */
	getAvailableOptions: (job: Job, optionsKey: GeoJSONOptionsKey) => GroupedGeoJSONOption[];

	/** Find the matching option for the current selection */
	getSelectedOption: (
		availableOptions: GroupedGeoJSONOption[],
		currentGeoJSON?: FeatureCollection,
	) => GroupedGeoJSONOption | null;

	/** Group options by their group label for menu display */
	getGroupedMenuOptions: (availableOptions: GroupedGeoJSONOption[]) => Record<string, GroupedGeoJSONOption[]>;

	/** Update job with new GeoJSON selection */
	updateJobGeoJSON: (jobId: string, geoJSONKey: GeoJSONFileKey, newGeoJSON?: FeatureCollection) => void;

	/** Update job with uploaded GeoJSON (also adds to fileOptions) */
	updateJobWithUpload: (
		jobId: string,
		optionsKey: GeoJSONOptionsKey,
		geoJSONKey: GeoJSONFileKey,
		newGeoJSON: FeatureCollection,
		fileName: string,
	) => void;

	/** Handle file upload */
	handleUpload: (
		jobId: string,
		optionsKey: GeoJSONOptionsKey,
		geoJSONKey: GeoJSONFileKey,
		file: File,
		setIsLoading: (loading: boolean) => void,
		resetFileInput: () => void,
	) => Promise<void>;

	/** Handle file download - format defaults to 'shapefile' for backward compatibility */
	handleDownload: (
		currentGeoJSON: FeatureCollection,
		selectedOption: GroupedGeoJSONOption,
		setIsLoading: (loading: boolean) => void,
		format?: GeoJSONDownloadFormat,
	) => Promise<void>;

	/** Delete file (clears files AND removes from fileOptions) */
	handleDelete: (
		jobId: string,
		optionsKey: GeoJSONOptionsKey,
		geoJSONKey: GeoJSONFileKey,
		currentGeoJSON: FeatureCollection,
	) => void;
}
